import { useEffect, useState } from "react";
import { Palette, Type } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Slider } from "./ui/slider";
import { DEFAULT_SIZES, SIZE_RANGES } from "../lib/inviteLayout";

const COLORS = ["#FFFFFF", "#0F172A", "#1D4ED8", "#EC4899", "#F59E0B", "#10B981", "#8B5CF6", "#EF4444"];
const SIZES = [["s", "S", 0.8], ["m", "M", 1], ["l", "L", 1.25], ["xl", "XL", 1.5]];
const HEX = /^#[0-9a-fA-F]{6}$/;
const RAINBOW = "conic-gradient(#ef4444,#f59e0b,#10b981,#06b6d4,#1d4ed8,#8b5cf6,#ec4899,#ef4444)";
const ring = (on) => (on ? "ring-2 ring-blue-700 ring-offset-1" : "");
const round1 = (v) => Math.round(v * 10) / 10;

const CustomColor = ({ id, value, onChange }) => {
  const [hex, setHex] = useState(value || "");
  useEffect(() => setHex(value || ""), [value]);
  const isCustom = value && !COLORS.includes(value);
  const typeHex = (v) => {
    setHex(v);
    if (HEX.test(v)) onChange(v.toUpperCase());
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`color-${id}-custom-btn`} aria-label="Custom color" style={{ background: isCustom ? value : RAINBOW }} className={`h-5 w-5 shrink-0 rounded-full border border-slate-300 transition-transform duration-200 hover:scale-110 ${ring(isCustom)}`} />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 space-y-3 p-3">
        <div className="flex items-center justify-between text-xs">
          <span className="font-bold uppercase tracking-[0.15em] text-slate-500">Custom</span>
          <span data-testid={`color-${id}-value`} className="font-mono text-slate-500">{value || "Auto"}</span>
        </div>
        <div className="flex items-center gap-2">
          <input type="color" data-testid={`color-${id}-custom-input`} value={value || "#1D4ED8"} onChange={(e) => onChange(e.target.value.toUpperCase())} className="h-8 w-10 cursor-pointer rounded border border-slate-200 bg-white p-0.5" />
          <input data-testid={`color-${id}-hex-input`} value={hex} onChange={(e) => typeHex(e.target.value.trim())} placeholder="#RRGGBB" maxLength={7} className="h-8 min-w-0 flex-1 rounded-lg border border-slate-200 px-2 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-blue-700" />
        </div>
      </PopoverContent>
    </Popover>
  );
};

const CompactColor = ({ id, value, onChange }) => (
  <div data-testid={`color-field-${id}`} className="flex flex-wrap items-center gap-1 sm:gap-1.5">
    <Palette className="h-3.5 w-3.5 text-slate-400" aria-label="Color" />
    <button type="button" data-testid={`color-${id}-default`} aria-pressed={!value} onClick={() => onChange(null)} className={`h-5 rounded-full border border-slate-300 bg-white px-2 text-[10px] font-semibold text-slate-600 hover:bg-slate-50 ${ring(!value)}`}>
      Auto
    </button>
    {COLORS.map((c) => (
      <button key={c} type="button" aria-label={c} aria-pressed={value === c} data-testid={`color-${id}-swatch-${c.slice(1).toLowerCase()}`} onClick={() => onChange(c)} style={{ backgroundColor: c }} className={`h-5 w-5 shrink-0 rounded-full border border-slate-300 transition-transform duration-200 hover:scale-110 ${ring(value === c)}`} />
    ))}
    <CustomColor id={id} value={value} onChange={onChange} />
  </div>
);

const CompactSize = ({ kind, value, onChange }) => {
  const [min, max] = SIZE_RANGES[kind];
  const current = value || DEFAULT_SIZES[kind];
  return (
    <div data-testid={`size-field-${kind}`} className="flex items-center gap-2">
      <Type className="h-3.5 w-3.5 text-slate-400" aria-label="Size" />
      <div className="inline-flex rounded-md bg-white p-0.5 ring-1 ring-slate-200">
        {SIZES.map(([id, label, factor]) => {
          const v = round1(DEFAULT_SIZES[kind] * factor);
          const on = round1(current) === v;
          return (
            <button key={id} type="button" data-testid={`size-${kind}-${id}`} aria-pressed={on} onClick={() => onChange(id === "m" ? null : v)} className={`rounded px-1.5 py-0.5 text-[11px] font-semibold transition-[background-color,color] duration-200 ${on ? "bg-blue-700 text-white" : "text-slate-500 hover:text-slate-800"}`}>
              {label}
            </button>
          );
        })}
      </div>
      <Slider data-testid={`size-${kind}-slider`} className="w-24 sm:w-28 [&_[role=slider]]:h-3.5 [&_[role=slider]]:w-3.5 [&>span:first-child]:h-1" min={min} max={max} step={0.1} value={[current]} onValueChange={([v]) => onChange(round1(v))} aria-label={`${kind} size`} />
      <span data-testid={`size-${kind}-value`} className="w-9 font-mono text-[10px] text-slate-400">{value ? `${current}%` : "Auto"}</span>
    </div>
  );
};

export const StyleRow = ({ kind, form, set }) => (
  <div data-testid={`text-style-${kind}`} className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg bg-slate-50 px-2.5 py-2">
    <CompactColor id={kind} value={form[`${kind}_color`]} onChange={set(`${kind}_color`)} />
    <CompactSize kind={kind} value={form[`${kind}_size`]} onChange={set(`${kind}_size`)} />
  </div>
);
